import React, { useState, useEffect, useCallback } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonDrawer from "src/components/CommonDrawer/CommonDrawer";

const GET_JSON_BY_APPROVAL_ID = "get-json-by-approval-id";

/** Cache JSON by approval ID for the session; cleared only on browser refresh. */
const jsonCache: Record<number, string> = {};

/**
 * Converts JSON string to readable "key: value" lines (no curly braces or quotes),
 * matching the internal app's Submission JSON display.
 */
function formatJsonForDisplay(jsonString: string): string {
  if (!jsonString || jsonString.trim() === "") return "";
  try {
    const jsonObj = JSON.parse(jsonString);
    if (typeof jsonObj === "string") return jsonObj;

    const formatObject = (obj: Record<string, unknown>, indent = 0): string => {
      const indentStr = "  ".repeat(indent);
      let result = "";

      for (const [key, value] of Object.entries(obj)) {
        if (value === null || value === undefined) continue;

        if (typeof value === "object" && !Array.isArray(value)) {
          result += `${indentStr}${key}:\n`;
          result += formatObject(value as Record<string, unknown>, indent + 1);
        } else if (Array.isArray(value)) {
          const arrStr = value.map((item) =>
            typeof item === "object" && item !== null ? JSON.stringify(item) : String(item),
          ).join(", ");
          result += `${indentStr}${key}: ${arrStr}\n`;
        } else {
          result += `${indentStr}${key}: ${value}\n`;
        }
      }

      return result;
    };

    return formatObject(jsonObj as Record<string, unknown>).trim();
  } catch {
    return jsonString;
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerBody: {
      position: "relative",
      minHeight: "60vh",
    },
    jsonContent: {
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      fontSize: "14px",
      lineHeight: "1.6",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      backgroundColor: "#f5f5f5",
      padding: theme.spacing(3),
      borderRadius: "4px",
      maxHeight: "70vh",
      overflow: "auto",
    },
    loaderOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255, 255, 255, 0.85)",
      zIndex: theme.zIndex.modal - 1,
      gap: theme.spacing(2),
    },
    errorText: {
      color: theme.palette.error.main,
      padding: theme.spacing(2),
    },
  }),
);

export interface GetJsonByApprovalIdResponse {
  success?: boolean;
  jsonData?: string;
}

interface JsonDataDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, drawer fetches JSON from get-json-by-approval-id and displays it. Takes precedence over jsonData. */
  approvalId: number | null;
  /** Optional pre-loaded JSON; used only when approvalId is null (e.g. tests or fallback). */
  jsonData?: string;
}

export const JsonDataDrawer: React.FC<JsonDataDrawerProps> = ({
  isOpen,
  onClose,
  approvalId,
  jsonData = "",
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [fetchedJson, setFetchedJson] = useState<string>("");
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchJsonByApprovalId = useCallback(async (id: number) => {
    const cached = jsonCache[id];
    if (cached !== undefined) {
      setFetchedJson(cached);
      setFetchError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFetchError(null);
    setFetchedJson("");
    try {
      const result = (await fetchWrapper.get(
        `${GET_JSON_BY_APPROVAL_ID}?approval_id=${encodeURIComponent(id)}&includeDocuments=false`,
      )) as GetJsonByApprovalIdResponse;
      if (result?.success && result.jsonData !== undefined) {
        const str =
          typeof result.jsonData === "string" ? result.jsonData : JSON.stringify(result.jsonData);
        jsonCache[id] = str;
        setFetchedJson(str);
      } else {
        setFetchError("No data returned.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load JSON";
      setFetchError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFetchError(null);
      setLoading(false);
      return;
    }
    if (approvalId !== null && approvalId !== undefined) {
      fetchJsonByApprovalId(approvalId);
    } else {
      setFetchedJson("");
      setLoading(false);
      setFetchError(null);
    }
  }, [isOpen, approvalId, fetchJsonByApprovalId]);

  const displayJson = approvalId != null ? fetchedJson : jsonData;
  const showLoader = approvalId != null && loading;
  const showError = approvalId != null && !loading && fetchError != null;
  const showContent =
    approvalId != null ? !loading && !fetchError : Boolean(jsonData && jsonData.trim() !== "");
  const contentText =
    displayJson && displayJson.trim() !== "" ? formatJsonForDisplay(displayJson) : "No Data";

  return (
    <CommonDrawer
      heading="Submission JSON Data"
      isOpen={isOpen}
      closeDrawer={onClose}
      drawerWidth={800}
    >
      <Box mt={2} className={classes.drawerBody}>
        {showError && (
          <Typography className={classes.errorText} data-testid="json-drawer-error">
            {fetchError}
          </Typography>
        )}
        {showContent && (
          <Box className={classes.jsonContent} data-testid="json-drawer-content">
            {contentText}
          </Box>
        )}
        {showLoader && (
          <Box className={classes.loaderOverlay} data-testid="json-drawer-loader">
            <CircularProgress size={40} />
            <Typography variant="body2">Loading...</Typography>
          </Box>
        )}
      </Box>
    </CommonDrawer>
  );
};
